import React, { useEffect, useContext, useState, useCallback } from 'react';
import _ from 'lodash';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { MLDisclosureUtil } from 'ml-portals-util-js';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { MLWizardNavUtil } from 'ml-portals-util-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';

import AddLossFormComponent from '../../components/AddLossFormComponent/AddLossFormComponent';
import AddIncidentFormComponent from '../../components/AddIncidentFormComponent/AddIncidentFormComponent';

import metadata from './DisclosuresPage.metadata.json5';
import messages from './DisclosuresPage.messages';
import styles from './DisclosuresPage.module.scss'

import { IntlContext } from '@jutro/legacy/locale';

const DisclosuresPage = (props) => {
    const { wizardData: submissionVM, updateWizardData, goNext, currentStep } = props;

    const {nextStepVisited} = MLWizardNavUtil(props);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const lobModel = submissionVM.lobData.personalUmbrella.coverables;
    const discloseExcludedDriver = submissionVM.baseData.canDiscloseExcludedDrivers_ML.value;

    const LOSS_TYPE_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('LossType_ML');
    const INCIDENT_DESCRIPTION_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentDescription_ML');
    const INCIDENT_CATEGORY_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentCategory_ML');
    const DISCLOSURE_TYPES = {
        ACCIDENT: INCIDENT_CATEGORY_TYPELIST.getCode('accident').code,
        CLAIM: INCIDENT_CATEGORY_TYPELIST.getCode('other_claim').code,
        VIOLATION: INCIDENT_CATEGORY_TYPELIST.getCode('violation').code,
        LOSS: 'loss'
    };

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const getTypeKeyDescription = useCallback((typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    }, [translator]);

    const filterDrivers = (driver, type) => {
        return _.get(driver, 'type_ML') === type || _.get(driver, 'driverType') === type;
    };

    const getDrivers = () => {
        let drivers = _.get(lobModel, 'value.vehicleOperators', []);
        drivers = drivers.filter((driver) => filterDrivers(driver, 'rated')
        || (filterDrivers(driver, 'excluded') && discloseExcludedDriver));
        return drivers;
    };

    const getDisclosuresFromDriver = (driver) => {
        return driver.disclosures_ML || driver.disclosures || driver.disclosure;
    };

    const [owners, setOwners] = useState([]);
    const [losses, setLosses] = useState(_.get(lobModel, 'homeLosses_ML.value', []));
    const [accidents, setAccidents] = useState([]);
    const [claims, setClaims] = useState([]);
    const [violations, setViolations] = useState([]);

    const [isAddLossVisible, setIsAddLossVisible] = useState(false);
    const [isAddIncidentVisible, setIsAddIncidentVisible] = useState(false);

    const getIncidents = useCallback(() => {
        return [...accidents, ...claims, ...violations];
    }, [accidents, claims, violations]);

    const initDisclosures = () => {
        const drivers = getDrivers();
        drivers.forEach((driver) => {
            setOwners((prevState) => [...prevState, driver.contact]);
            const disclosures = getDisclosuresFromDriver(driver);
            if (disclosures) {
                if (!_.isNil(disclosures.accidents)) {
                    setAccidents((prevState) => [...prevState, ...disclosures.accidents]);
                }
                if (!_.isNil(disclosures.claims)) {
                    setClaims((prevState) => [...prevState, ...disclosures.claims]);
                }
                if (!_.isNil(disclosures.violations)) {
                    setViolations((prevState) => [...prevState, ...disclosures.violations]);
                }
            }
        });
    };

    useEffect(() => {
        initDisclosures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        accidents.forEach((accident) => { _.set(accident, 'originalOwner', accident.person.publicID); });
    }, [accidents]);

    useEffect(() => {
        claims.forEach((claim) => { _.set(claim, 'originalOwner', claim.person.publicID); });
    }, [claims]);

    useEffect(() => {
        violations.forEach((violation) => { _.set(violation, 'originalOwner', violation.person.publicID); });
    }, [violations]);

    const createNewDisclosure = (discCategory) => {
        const newDisclosureFields = { source: 'customer_disclosed' };
        let newDisclosure;
        switch (discCategory) {
            case (DISCLOSURE_TYPES.ACCIDENT):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.AccidentDTOML');
                newDisclosure.paidAmount = { amount: undefined };
                newDisclosure.value.category = discCategory;
                newDisclosure.value.isCategoryRequired = true;
                break;
            case (DISCLOSURE_TYPES.CLAIM):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.ClaimDTOML');
                newDisclosure.paidAmount = { amount: undefined };
                newDisclosure.value.category = discCategory;
                newDisclosure.value.isCategoryRequired = true;
                break;
            case (DISCLOSURE_TYPES.VIOLATION):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.ViolationDTOML');
                newDisclosure.value.category = discCategory;
                newDisclosure.value.isCategoryRequired = true;
                break;
            case (DISCLOSURE_TYPES.LOSS):
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.LossDTOML');
                newDisclosure.paidAmount = { amount: undefined };
                break;
            default:
                newDisclosure = viewModelService.create(newDisclosureFields, 'pc',
                    'metlife.edge.capabilities.policycommon.disclosures.dto.AccidentDTOML');
                newDisclosure.paidAmount = { amount: undefined };
                newDisclosure.date = undefined;
                newDisclosure.value.category = undefined;
                newDisclosure.value.isCategoryRequired = true;
                break;
        }

        return newDisclosure;
    };

    const [lossToAdd, setLossToAdd] = useState(null);
    const [incidentToAdd, setIncidentToAdd] = useState(null);

    const showAddLossSection = () => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Add Claim`);
        setLossToAdd(createNewDisclosure(DISCLOSURE_TYPES.LOSS));
        setIsAddLossVisible(true);
    };

    const showAddIncidentSection = () => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Add Incident`);
        setIncidentToAdd(createNewDisclosure());
        setIsAddIncidentVisible(true);
    };

    const hideAddDisclosureSection = (type) => {
        switch (type) {
            case (DISCLOSURE_TYPES.ACCIDENT):
            case (DISCLOSURE_TYPES.VIOLATION):
            case (DISCLOSURE_TYPES.CLAIM):
            case (undefined):
                setIsAddIncidentVisible(false);
                MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add Incident`);
                break;
            default:
                setIsAddLossVisible(false);
                MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add Claim`);
                break;
        }
    };

    const finishAddingDisclosure = (disclosure) => {
        if (disclosure.aspects.valid && disclosure.aspects.subtreeValid) {
            if (disclosure.value.category && MLDisclosureUtil.isViolationType(disclosure.value.category)) {
                _.set(disclosure, 'paidAmount', undefined);
            }

            if (disclosure.paidAmount && disclosure.paidAmount.value) {
                _.set(disclosure, 'paidAmount.currency', 'usd');
            }

            switch (disclosure.value.category) {
                case (DISCLOSURE_TYPES.ACCIDENT):
                    setAccidents((prevState) => [...prevState, disclosure.value]);
                    hideAddDisclosureSection(disclosure.value.category);
                    break;
                case (DISCLOSURE_TYPES.CLAIM):
                    setClaims((prevState) => [...prevState, disclosure.value]);
                    hideAddDisclosureSection(disclosure.value.category);
                    break;
                case (DISCLOSURE_TYPES.VIOLATION):
                    setViolations((prevState) => [...prevState, disclosure.value]);
                    hideAddDisclosureSection(disclosure.value.category);
                    break;
                default: // losses don't have a category
                    setLosses((prevState) => [...prevState, disclosure.value]);
                    hideAddDisclosureSection(DISCLOSURE_TYPES.LOSS);
                    break;
            }
        }
    };

    const removePriorDisclosure = (disclosure, type) => {
        let idx = -1;
        switch (type) {
            case (DISCLOSURE_TYPES.ACCIDENT):
                idx = accidents.indexOf(disclosure);
                if (idx > -1) {
                    setAccidents((prevState) => [...prevState.slice(0, idx), ...prevState.slice(idx + 1)]);
                }
                MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Incident`);
                break;
            case (DISCLOSURE_TYPES.CLAIM):
                idx = claims.indexOf(disclosure);
                if (idx > -1) {
                    setClaims((prevState) => [...prevState.slice(0, idx), ...prevState.slice(idx + 1)]);
                }
                MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Incident`);
                break;
            case (DISCLOSURE_TYPES.VIOLATION):
                idx = violations.indexOf(disclosure);
                if (idx > -1) {
                    setViolations((prevState) => [...prevState.slice(0, idx), ...prevState.slice(idx + 1)]);
                }
                MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Incident`);
                break;
            case (DISCLOSURE_TYPES.LOSS):
                idx = losses.indexOf(disclosure);
                if (idx > -1) {
                    setLosses((prevState) => [...prevState.slice(0, idx), ...prevState.slice(idx + 1)]);
                }
                MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Claim`);
                break;
            default:
                break;
        }
    };

    const onIncidentCategoryChange = (incidentCategoryChange) => {
        const previousDate = incidentToAdd.date.value;
        const previousPerson = incidentToAdd.person.value;
        const newIncident = createNewDisclosure(incidentCategoryChange);
        newIncident.date.value = previousDate;
        newIncident.person.value = previousPerson;
        setIncidentToAdd(newIncident);
    };

    const hasLosses = () => {
        return losses.length > 0;
    };

    const hasIncidents = () => getIncidents().length > 0;

    const hasPrefilledLosses = () => losses.some((loss) => loss.source !== 'customer_disclosed');

    const hasPrefilledIncidents = () => getIncidents().some((acv) => acv.source !== 'customer_disclosed');

    const isCustomerDisclosure = (d) => d.source === 'customer_disclosed';

    const nextStepText_Ext = () => {
        return (
            <FormattedMessage
                {...messages.nextDescription}
                values={{
                    action: (
                        <strong>{translator(messages.reviewYourQuote)}</strong>
                    )
                }}
            />
        );
    };

    const generateLossesOverrides = () => {
        const overrides = losses.map((loss, index) => {
            return {
                [`lossDateCompleteIcon${index}`]: {
                    visible: isCustomerDisclosure(loss)
                },
                [`lossDate${index}`]: {
                    content: getFormattedDateString(loss.date, true)
                },
                [`lossDescription${index}`]: {
                    content: getTypeKeyDescription(LOSS_TYPE_TYPELIST.getCode(loss.type).name)
                },
                [`lossAmount${index}`]: {
                    tag: 'span',
                    currency: loss.paidAmount.currency,
                    value: loss.paidAmount.amount,
                    showFractions: true
                },
                [`deleteLoss${index}`]: {
                    visible: isCustomerDisclosure(loss),
                    onClick: () => {
                        removePriorDisclosure(loss, DISCLOSURE_TYPES.LOSS);
                    }
                }
            };
        });
        return Object.assign({}, ...overrides);
    };

    const generateIncidentsOverrides = () => {
        const overrides = getIncidents().map((incident, index) => {
            return {
                [`incidentDateCompleteIcon${index}`]: {
                    visible: isCustomerDisclosure(incident)
                },
                [`incidentDate${index}`]: {
                    content: getFormattedDateString(incident.date, true)
                },
                [`incidentDriver${index}`]: {
                    content: incident.person.displayName
                },
                [`incidentCategory${index}`]: {
                    content: getTypeKeyDescription(INCIDENT_CATEGORY_TYPELIST.getCode(incident.category).name)
                },
                [`incidentDescription${index}`]: {
                    content: getTypeKeyDescription(INCIDENT_DESCRIPTION_TYPELIST.getCode(incident.type).name)
                },
                [`incidentAmount${index}`]: {
                    tag: 'span',
                    currency: _.get(incident, 'paidAmount.currency'),
                    value: _.get(incident, 'paidAmount.amount'),
                    showFractions: true
                },
                [`deleteIncident${index}`]: {
                    visible: isCustomerDisclosure(incident),
                    onClick: () => {
                        removePriorDisclosure(incident, incident.category);
                    }
                }
            };
        });
        return Object.assign({}, ...overrides);
    };

    const overrides = {
        lossesContainer: {
            visible: hasLosses()
        },
        lossesHeader: {
            content: translator(hasPrefilledLosses()
                ? messages.hasPrefilledLossesHeader
                : messages.hasNoPrefilledLossesHeader)
        },
        lossesTooltip: {
            visible: hasPrefilledLosses(),
            tooltipLabel: messages.tooltipLabel,
            tooltipContent: messages.tooltipContent
        },
        lossesIterable: {
            data: losses
        },
        addLossButton: {
            visible: !isAddLossVisible,
            onClick: () => {
                showAddLossSection();
            }
        },
        addLossForm: {
            visible: isAddLossVisible,
            model: lossToAdd,
            onSave: (loss) => finishAddingDisclosure(loss),
            onCancel: () => {
                hideAddDisclosureSection(DISCLOSURE_TYPES.LOSS);
                setLossToAdd(null);
            },
            onModelChange: () => updateWizardData(submissionVM)
        },
        incidentsContainer: {
            visible: hasIncidents()
        },
        incidentsHeader: {
            content: translator(hasPrefilledIncidents()
                ? messages.hasPrefilledIncidentsHeader
                : messages.hasNoPrefilledIncidentsHeader)
        },
        incidentsTooltip: {
            visible: hasPrefilledIncidents(),
            tooltipLabel: messages.tooltipLabel,
            tooltipContent: messages.tooltipContent
        },
        incidentsIterable: {
            data: getIncidents()
        },
        addIncidentButton: {
            visible: !isAddIncidentVisible,
            onClick: () => {
                showAddIncidentSection();
            }
        },
        addIncidentForm: {
            visible: isAddIncidentVisible,
            model: incidentToAdd,
            owners,
            onSave: (incident) => finishAddingDisclosure(incident),
            onCancel: (incident) => {
                hideAddDisclosureSection(_.get(incident, 'category.value.code'));
                setIncidentToAdd(null);
            },
            onModelChange: () => updateWizardData(submissionVM),
            onIncidentCategoryChange: (value) => {
                onIncidentCategoryChange(value);
            }
        },
        nextDescription: {
            content: nextStepText_Ext()
        },
        ...generateLossesOverrides(),
        ...generateIncidentsOverrides()
    };

    const onNext = async() => {
        if (submissionVM.aspects.valid && submissionVM.aspects.subtreeValid) {
            _.set(submissionVM, 'lobData.personalUmbrella.coverables.homeLosses_ML.value', losses);
            const drivers = getDrivers();
            drivers.forEach((driver) => {
                _.set(driver, 'disclosures.accidents', accidents.filter((accident) => accident.person.publicID === driver.contact.publicID));
                _.set(driver, 'disclosures.claims', claims.filter((claim) => claim.person.publicID === driver.contact.publicID));
                _.set(driver, 'disclosures.violations', violations.filter((violation) => violation.person.publicID === driver.contact.publicID));
            });

            showProgressDialog(platformMessages.saveSubmission);
            try {
                const res = await LoadSaveService.saveAndQuoteSubmission(submissionVM.value);
                submissionVM.value = res;
                MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'quoteDetails');
                if (!nextStepVisited()) {
                    return submissionVM;
                }
                goNext();
            } finally {
                hideProgressDialog(platformMessages.saveSubmission);
            }
        }
        return false;
    };

    const resolvers = {
        resolveComponentMap: {
            AddLossFormComponent,
            AddIncidentFormComponent
        },
        resolveCallbackMap: {
            onNext
        }
    };

    const skipOnResumeIf = () => {
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === currentStep.mlStepName) {
            return false;
        }

        return true;
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            skipWhen={skipOnResumeIf}
            showNext={!nextStepVisited()}
            onNext={onNext}
            nextLabel={translator(platformMessages.mlContinue)}
            showTextNextToButton={!nextStepVisited()}
            textNextToButton={nextStepText_Ext()}
            metadataPageId={!nextStepVisited() && _.get(metadata, 'pageContent.id')}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
};

DisclosuresPage.propTypes = wizardProps;
export default DisclosuresPage;
